// src/hooks/useStripe.js - Updated for Run Payments with Stripe Extension
import { useState, useEffect, createContext, useContext } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from './useAuth';
import { collection, addDoc, onSnapshot, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Initialize Firebase Functions for the correct region (us-east1)
const functions = getFunctions(undefined, 'us-east1');

// Create Stripe Context
const StripeContext = createContext();

// Custom hook to use stripe context
export const useStripe = () => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
};

// Stripe Provider Component
export const StripeProvider = ({ children }) => {
  const { user } = useAuth();
  const [stripe, setStripe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Initialize Stripe
  useEffect(() => {
    const initStripe = async () => {
      try {
        const stripeInstance = await stripePromise;
        setStripe(stripeInstance);
      } catch (err) {
        setError('Failed to load Stripe');
        console.error('Stripe initialization error:', err);
      } finally {
        setLoading(false);
      }
    };

    initStripe();
  }, []);

  // Create payment checkout session with dynamic pricing
  const createPaymentCheckout = async (paymentData) => {
    if (!paymentData.customerId) {
      throw new Error('Customer ID is required');
    }

    setProcessingPayment(true);
    setError(null);

    try {
      // Calculate total amount in cents
      const totalAmount = Math.round(paymentData.items.reduce((sum, item) => 
        sum + (item.amount * (item.quantity || 1)), 0
      ) * 100);

      // Generate a document reference with auto-generated ID first
      const docRef = doc(collection(db, 'customers', paymentData.customerId, 'checkout_sessions'));

      // Create checkout session with the ID already known
      await setDoc(docRef, {
        mode: 'payment', // One-time payment
        line_items: [
          {
            price_data: {
              currency: paymentData.currency || 'usd',
              unit_amount: totalAmount,
              product_data: {
                name: paymentData.items[0]?.description || 'Landscaping Service',
                description: paymentData.description || `Service for ${paymentData.customerEmail}`,
              },
            },
            quantity: 1,
          },
        ],
        success_url: paymentData.successUrl || `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: paymentData.cancelUrl || `${window.location.origin}/dashboard/payments?cancelled=true`,
        metadata: {
          firebase_uid: paymentData.customerId,
          customer_email: paymentData.customerEmail,
          quote_id: paymentData.quoteId || '',
          service_type: paymentData.serviceType || 'landscaping',
          property_address: paymentData.propertyAddress || '',
          created_by: user?.uid || 'system',
          checkout_session_id: docRef.id, // Include the checkout session ID!
          payment_type: paymentData.metadata?.payment_type || 'one_time_service',
          created_from: paymentData.metadata?.created_from || 'quote_approval',
          ...paymentData.metadata
        }
      });

      console.log('Created checkout session with ID:', docRef.id);

      // Listen for checkout URL to be populated by extension
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Checkout session creation timed out after 30 seconds'));
        }, 30000);

        const unsubscribe = onSnapshot(docRef, (doc) => {
          const data = doc.data();
          
          if (data.url) {
            clearTimeout(timeout);
            unsubscribe();
            resolve({
              checkoutId: doc.id,
              url: data.url,
              sessionId: data.id
            });
          }
          
          if (data.error) {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error(data.error.message || 'Failed to create checkout session'));
          }
        });
      });

    } catch (err) {
      console.error('Error creating payment checkout:', err);
      setError(err.message);
      throw err;
    } finally {
      setProcessingPayment(false);
    }
  };

  // Create subscription checkout session (for recurring services)
  const createSubscriptionCheckout = async (priceId, options = {}) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated');
    }

    setProcessingPayment(true);
    setError(null);

    try {
      const docRef = await addDoc(
        collection(db, 'customers', user.uid, 'checkout_sessions'),
        {
          price: priceId,
          success_url: options.successUrl || `${window.location.origin}/dashboard/payments?success=true`,
          cancel_url: options.cancelUrl || `${window.location.origin}/dashboard/payments?canceled=true`,
          trial_period_days: options.trialDays,
          allow_promotion_codes: options.allowPromotionCodes,
          metadata: options.metadata || {}
        }
      );

      // Listen for checkout URL
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Subscription checkout creation timed out'));
        }, 30000);

        const unsubscribe = onSnapshot(docRef, (doc) => {
          const data = doc.data();
          if (data.url) {
            clearTimeout(timeout);
            unsubscribe();
            resolve({
              checkoutId: doc.id,
              url: data.url
            });
          }
          if (data.error) {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error(data.error.message));
          }
        });
      });
      
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setProcessingPayment(false);
    }
  };

  // Create customer portal session
  const getCustomerPortalUrl = async (returnUrl) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated');
    }

    try {
      console.log('Creating customer portal session...');
      
      const createPortalLink = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
      
      const { data } = await createPortalLink({
        returnUrl: returnUrl || `${window.location.origin}/dashboard/payments`,
        locale: 'auto'
      });

      console.log('Portal URL created successfully');
      return data.url;
      
    } catch (err) {
      console.error('Error creating customer portal URL:', err);
      setError(`Failed to create portal session: ${err.message}`);
      throw err;
    }
  };

  // Create mobile payment (for future mobile app support)
  const createMobilePayment = async (amount, currency = 'usd') => {
    if (!user?.uid) {
      throw new Error('User must be authenticated');
    }

    setProcessingPayment(true);
    setError(null);

    try {
      const docRef = await addDoc(
        collection(db, 'customers', user.uid, 'checkout_sessions'),
        {
          client: 'mobile',
          mode: 'payment',
          amount: Math.round(amount * 100), // Convert to cents
          currency: currency
        }
      );

      // Listen for mobile payment secrets
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Mobile payment creation timed out'));
        }, 30000);

        const unsubscribe = onSnapshot(docRef, (doc) => {
          const data = doc.data();
          if (data.paymentIntentClientSecret && data.ephemeralKeySecret && data.customer) {
            clearTimeout(timeout);
            unsubscribe();
            resolve({
              paymentIntentClientSecret: data.paymentIntentClientSecret,
              ephemeralKeySecret: data.ephemeralKeySecret,
              customer: data.customer,
              publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
            });
          }
          if (data.error) {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error(data.error.message));
          }
        });
      });

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setProcessingPayment(false);
    }
  };

  // Clear error function
  const clearError = () => setError(null);

  const value = {
    stripe,
    loading,
    error,
    processingPayment,
    createPaymentCheckout,
    createSubscriptionCheckout,
    createMobilePayment,
    getCustomerPortalUrl,
    clearError
  };

  return <StripeContext.Provider value={value}>{children}</StripeContext.Provider>;
};